import { useAppSelector } from 'hooks/redux';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Typography from '@mui/material/Typography';
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';

import { filtersActions, filtersSelectors } from 'app-components/flux/filters';
import { freeToSpendActions, freeToSpendSelectors } from 'app-components/flux/free-to-spend';
import { rootUiActions } from 'app-components/flux/root-ui';
import { tracker } from 'app-components/utils/core';

const deleteCopy = Object.freeze({
  series:
    'Are you sure you want to delete this expense series from the current and all future months? This will remove it from the current and all future months. Money already spent this month will move to Other Spend, and any unspent funds will become available to use elsewhere.',
  thisMonth:
    'Are you sure you want to delete this expense for this month? Any money already spent in this category will move to Other Spend, and any unspent funds will be made available to use elsewhere.',
  nonRecurring: 'Are you sure you want to delete this expense?',
});

type DeletePlannedItemProps = {
  freeToSpendId: string;
  filterId: string;
  deleteSeries: boolean;
};

function DeletePlannedItem(props: DeletePlannedItemProps) {
  const { filterId, freeToSpendId, deleteSeries } = props;
  const navigate = useNavigate();
  const filter = useAppSelector((state) => filtersSelectors.getFilter(state, filterId));
  const dispatch = useDispatch();
  const freeToSpend = useAppSelector((state) => freeToSpendSelectors.getFreeToSpendById(state, freeToSpendId));
  const plannedItems = useAppSelector((state) =>
    freeToSpendSelectors.getPlannedSpendingItemsByF2SId(state, freeToSpendId),
  );
  const deletedPlannedItem = plannedItems.find((item) => item.filterId === filterId);
  const deleteMessage = deletedPlannedItem?.recurring
    ? deleteCopy[deleteSeries ? 'series' : 'thisMonth']
    : deleteCopy.nonRecurring;

  const deleteItem = () => {
    tracker.track(tracker.events.budgetItemDelete);

    const plannedSpendingItems = plannedItems.filter((item) => item.filterId !== filter?.id);
    if (deleteSeries) {
      // if deleting a recurring PSI series
      if (deletedPlannedItem) {
        dispatch(freeToSpendActions.deletePlannedSpending(deletedPlannedItem));
      }
      if (freeToSpend) {
        // optimistically remove planned spending from client resource (this isn't sent to QCS)
        dispatch(
          freeToSpendActions.updateFreeToSpendOptimistic(
            freeToSpend.merge({
              plannedSpendingItems,
            }),
          ),
        );
      }
    } else if (freeToSpend?.id) {
      // deleting single month instance
      dispatch(
        freeToSpendActions.updateFreeToSpend({
          id: freeToSpend.id,
          plannedSpendingItems,
          doPatch: true, // doPatch not in record but needed to clear planned spending items from existing object
        }),
      );
    }

    // add size check bc it becomes an issue for filters when planned items exceed around 50
    if (filter && (!deletedPlannedItem?.recurring || (deleteSeries && plannedSpendingItems?.size > 49))) {
      dispatch(filtersActions.deleteFilter(filter));
    }

    dispatch(rootUiActions.removeDialog());
  };

  const onClose = useCallback(() => dispatch(rootUiActions.removeDialog('delete-planned-dialog')), [dispatch]);

  return (
    <StdDialog
      onClose={onClose}
      open
      showCloseButton
      title={deleteSeries ? 'Delete expense series' : 'Delete this month’s expense'}
    >
      <StdDialogContent id='delete_planned_spending_dialog'>
        <Typography variant='titleRegular'>{deleteMessage}</Typography>
      </StdDialogContent>
      <StdDialogActions
        primary={{
          id: 'delete-delete-button',
          children: 'Delete',
          color: 'error',
          onClick: () => {
            navigate(`/spending-plan/planned-spending?date=${freeToSpend?.date || ''}`, { replace: true });
            setTimeout(deleteItem, 10); // This is done so we give time for navigation to complete before deleting the PSI. (The PSI ID can be in the URL, and can throw an error for INVALID ID)
          },
        }}
        secondary={{
          id: 'delete-cancel-button',
          onClick: onClose,
        }}
      />
    </StdDialog>
  );
}

DeletePlannedItem.displayName = 'DeletePlannedItem';

export default DeletePlannedItem;
